import {
  createFileRoute,
  useRouteContext,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import { type JSX, useMemo } from "react";
import { z } from "zod";

import { cn } from "@repo/cn";
import { graphql } from "@repo/graphql-types";
import {
  HomeNextEventsSectionQueryDocument,
  HomeNextSchedulesSectionQueryDocument,
  HomeSectionLivancePayQueryDocument,
  HomeSectionNextStepsQueryDocument,
  HomeSectionOneLivQueryDocument,
  NewHomeLivancePayCardsQueryDocument,
  NewHomeNextAppointmentsSectionQueryDocument,
  NewHomeNextEventsSectionQueryDocument,
  NewHomeOnlineBookingQueryDocument,
  NewHomeSectionLivanceAssistQueryDocument,
  NewHomeSectionLivancePayQueryDocument,
  OnelivMarketingCardQueryDocument,
  SiteMarketingCardQueryDocument,
} from "@repo/graphql-types/graphql";
import Plus from "@repo/icons/default/Plus";

import { HomeSectionLivanceAssist } from "@/components/home/assist/home-section-livance-assist";
import { HomeSectionChatbot } from "@/components/home/chat-bot/home-section-chat-bot";
import { FullScreenModalHome } from "@/components/home/full-screen-modal-home";
import { HomeHeaderCards } from "@/components/home/header/home-header-cards";
import { HomeProfileHeader } from "@/components/home/header/home-profile-header";
import { HomeSectionReferral } from "@/components/home/home-section-referral";
import { HomeNextEventsSection } from "@/components/home/next-events/home-next-events-section";
import { HomeNextSchedulesSection } from "@/components/home/next-schedules/home-next-schedules-section";
import { HomeSectionNextSteps } from "@/components/home/next-steps/home-section-next-steps";
import { HomeSectionOneLiv } from "@/components/home/oneliv/home-section-oneliv";
import { HomeSectionLivancePay } from "@/components/home/payment/home-section-livance-pay";
import { NewHomeSections } from "@/components/new-home/new-home-sections";
import { LatePaymentDrawer } from "@/components/payment/late-payment-drawer";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import { Fab } from "@/components/ui/fab/fab";
import { BottomBarV2 } from "@/components/ui/layout/bottom-bar/bottom-bar-v2";
import { Page } from "@/components/ui/layout/page/page";
import { PendingBillingNotificationProvider } from "@/hooks/providers/pending-billing-notification-provider";
import { useGraphQL, useInvalidateQuery } from "@/hooks/use-graphql";
import { trackEvent } from "@/lib/tracking";
import { shouldShowComponentByFF } from "@/utils/should-show-component-by-ff";

const HomePageQuery = graphql(`
  query HomePage($codUsuario: Int!) {
    ...HomeProfileHeaderFragment
    ...CancellationRequestCardFragment
  }
`);

const HomePage = (): JSX.Element => {
  const { user, flags } = useRouteContext({ from: "__root__" });

  const pullToRefreshIsActive = flags["ativa-pull-to-refresh-na-home"];
  const showAssistSectionFF = useMemo(
    () => flags["membro-pode-contratar-pacote-servicos"],
    [flags],
  );

  const showLatePaymentDrawerIsActive = useMemo(
    () => flags["exibe-fluxo-de-reexecutar-cobranca"],
    [flags],
  );

  const { routesWithBannerReferral } = useMemo(
    () => flags["exibir-banner-de-gentileza-gera-gentileza"],
    [flags],
  );

  const showHelpBanner = useMemo(
    () => flags["exibe-botoes-do-chatbot-zendesk-no-app"]?.banner ?? false,
    [flags],
  );

  const showNextStepsSection = useMemo(
    () => flags["exibe-sessao-proximos-passos-na-home"],
    [flags],
  );

  const showNextEventsSection = useMemo(
    () => Boolean(flags["exibe-proximos-eventos-na-home"]),
    [flags],
  );

  const showNewHome = useMemo(() => flags["exibe-nova-home"], [flags]);

  const queryResult = useGraphQL(HomePageQuery, {
    codUsuario: user.codUsuario,
  });

  const { data } = queryResult;

  const router = useRouter();
  const searchParams = useSearch({
    from: "/home/",
  });

  const trackClickedFabEvent = (): void => {
    trackEvent("Fab Clicado", {
      origem: "Home",
    });
  };

  const onClickFab = (): void => {
    trackClickedFabEvent();
    void router.navigate({
      to: ".",
      search: { action: "open-more-actions" },
    });
  };

  const onCloseModal = (): void => {
    router.history.back();
  };

  const isModalOpen = searchParams.action === "open-more-actions";

  const invalidateNextSchedulesQuery = useInvalidateQuery(
    HomeNextSchedulesSectionQueryDocument,
  );

  const invalidateNextEventsQuery = useInvalidateQuery(
    HomeNextEventsSectionQueryDocument,
  );

  const invalidateLivancePayQuery = useInvalidateQuery(
    HomeSectionLivancePayQueryDocument,
  );

  const invalidateOneLivQuery = useInvalidateQuery(HomeSectionOneLivQueryDocument);

  const invalidateNewHomeQueries = [
    useInvalidateQuery(NewHomeNextAppointmentsSectionQueryDocument),
    useInvalidateQuery(NewHomeSectionLivancePayQueryDocument),
    useInvalidateQuery(NewHomeLivancePayCardsQueryDocument),
    useInvalidateQuery(NewHomeSectionLivanceAssistQueryDocument),
    useInvalidateQuery(OnelivMarketingCardQueryDocument),
    useInvalidateQuery(NewHomeNextEventsSectionQueryDocument),
    useInvalidateQuery(NewHomeOnlineBookingQueryDocument),
    useInvalidateQuery(HomeSectionNextStepsQueryDocument),
    useInvalidateQuery(SiteMarketingCardQueryDocument),
  ];

  const handleRefresh = async (): Promise<void> => {
    await queryResult.refetch();
    invalidateNextEventsQuery();

    if (showNewHome) {
      for (const invalidate of invalidateNewHomeQueries) {
        invalidate();
      }
      return;
    }

    invalidateNextSchedulesQuery();
    invalidateLivancePayQuery();
    invalidateOneLivQuery();
  };

  return (
    <div
      className={cn(
        "flex flex-col flex-1 bg-neutral-100 overflow-hidden md:overflow-visible",
        showNewHome && "bg-neutral-50",
      )}
    >
      <AsyncDataWrapper {...queryResult}>
        {data ? (
          <PendingBillingNotificationProvider>
            <div
              className={cn(
                "py-4 bg-primary-400 flex flex-col rounded-b-2xl md:rounded-none",
                showNewHome && "pb-0 rounded-b-none md:pb-4",
              )}
            >
              <HomeProfileHeader data={data} />
              {showNewHome ? null : <HomeHeaderCards data={data} />}
            </div>
            <Page
              className={cn(
                "px-0 pt-0",
                showNewHome &&
                  "w-full max-w-none bg-linear-to-b/srgb from-primary-400 from-50% to-neutral-50 to-50% md:max-w-none md:bg-none [&_.ptr]:overflow-clip [&_.ptr__children]:overflow-clip",
              )}
              id="bottom-bar-v2-padding-bottom"
              onRefresh={pullToRefreshIsActive ? handleRefresh : undefined}
            >
              {showNewHome ? (
                <>
                  <div className="sticky top-0 pb-8 bg-primary-400 md:static md:pb-0 md:[&:has(>*>*)]:pb-4">
                    <HomeHeaderCards data={data} />
                  </div>
                  <div className="relative z-10 -mt-4 min-h-dvh bg-neutral-50 rounded-t-[1.25rem] md:mt-0 md:min-h-0 md:rounded-none">
                    <NewHomeSections />
                  </div>
                </>
              ) : (
                <div className="py-6 flex flex-col gap-6">
                  {showNextStepsSection ? <HomeSectionNextSteps /> : null}
                  <div>
                    <HomeNextSchedulesSection />
                  </div>
                  {showNextEventsSection ? (
                    <div>
                      <HomeNextEventsSection />
                    </div>
                  ) : null}
                  {showHelpBanner ? (
                    <div>
                      <HomeSectionChatbot />
                    </div>
                  ) : null}
                  {shouldShowComponentByFF(routesWithBannerReferral) ? (
                    <HomeSectionReferral />
                  ) : null}
                  {showAssistSectionFF ? <HomeSectionLivanceAssist /> : null}
                  <HomeSectionLivancePay />
                  <HomeSectionOneLiv />
                </div>
              )}
            </Page>
            <Fab onClick={onClickFab}>
              <Plus size={32} className="fill-white" />
            </Fab>
            <FullScreenModalHome isOpen={isModalOpen} onClose={onCloseModal} />
            {showLatePaymentDrawerIsActive ? <LatePaymentDrawer /> : null}
          </PendingBillingNotificationProvider>
        ) : null}
      </AsyncDataWrapper>
      <BottomBarV2 />
    </div>
  );
};

export const Route = createFileRoute("/home/")({
  component: HomePage,
  validateSearch: z.object({
    action: z.string().optional(),
  }),
});
